import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { AxiosRequestConfig } from 'axios';
import api from '@/connections/axios.instance';
import type { TypeApiError } from '@/types/api/types.api';

export type TypeQueryArgs = {
    url: string;
    method?: AxiosRequestConfig['method'];
    data?: unknown;
    params?: Record<string, unknown>;
    timeout?: number;
};

type TypeRequestOptions = { fallback?: string; timeoutMessage?: string };

const STEP_UP_DETAILS = ['acr', 'maxAgeSeconds'];

const fieldsFrom = (payload: unknown, code: string | null): Record<string, string> | null => {
    const source = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload as Record<string, unknown> : {};
    const entries = Object.entries(source)
        .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
        .filter(([key]) => code !== 'STEP_UP_REQUIRED' || !STEP_UP_DETAILS.includes(key));
    if (code && !entries.some(([key]) => key === 'code')) entries.push(['code', code]);
    return entries.length ? (Object.fromEntries(entries) as Record<string, string>) : null;
};

const codeFrom = (body: unknown): string | null => {
    const code = (body as { code?: unknown } | null)?.code;
    return typeof code === 'string' && /^[A-Z_]{2,64}$/.test(code) ? code : null;
};

export const axiosRequest = async (
    { url, method = 'get', data, params, timeout }: TypeQueryArgs,
    { fallback = 'Richiesta non riuscita', timeoutMessage }: TypeRequestOptions = {},
): Promise<{ data: unknown } | { error: TypeApiError }> => {
    try {
        const response = await api({ url, method, data, params, ...(timeout ? { timeout } : {}) });
        return { data: response.data?.data ?? null };
    } catch (error: any) {
        const response = error?.response;
        const code = codeFrom(response?.data);
        return {
            error: {
                status: response?.status ?? 0,
                message: response?.data?.message ?? (timeoutMessage && error?.code === 'ECONNABORTED' ? timeoutMessage : error?.message ?? fallback),
                fields: fieldsFrom(response?.data?.data, code),
                code,
            },
        };
    }
};

const axiosBaseQuery: BaseQueryFn<TypeQueryArgs, unknown, TypeApiError> = (args) => axiosRequest(args);

export const dashboardApi = createApi({
    reducerPath: 'api',
    baseQuery: axiosBaseQuery,
    tagTypes: ['Customer', 'Project', 'Board', 'Tag', 'Task', 'CatalogItem', 'CatalogBundle', 'Quote', 'Invoice', 'Expense', 'Event', 'Person', 'Account', 'Session', 'Device', 'Passkey', 'Access', 'GatewayService', 'GatewayKey', 'GatewayRoute', 'GatewayEndpoint', 'GatewayAudit', 'GatewayAccess', 'GatewayDiagnose', 'Traffic', 'Company', 'Mail', 'Recurring', 'DayNote', 'Connection', 'Conversation', 'ConversationDraft', 'ConversationTemplate', 'ChatReceipts', 'CalendarFeed', 'ExpenseSummary', 'ExpenseReview', 'ExpenseTotals'],
    endpoints: () => ({}),
});

export default dashboardApi;
